import React, { useState, useEffect } from 'react';
import { Printer, Download, Check, X, ShoppingCart } from 'lucide-react';

/**
 * Materials Summary Component
 * 
 * Displays a summary of all materials needed for a bottling plan,
 * organized by material type with options to print, export, and mark items for ordering.
 * 
 * @param {Object} props 
 * @param {Array} props.items - Bottling plan items
 * @param {Object} props.materialsByType - Materials organized by type
 */
const MaterialsSummary = ({ items = [], materialsByType = {}, onGenerateOrder }) => {
  const [materialSummary, setMaterialSummary] = useState({
    bottles: [],
    corks: [],
    capsules: [],
    labels: []
  });
  const [checkedItems, setCheckedItems] = useState({});
  const [showPrintView, setShowPrintView] = useState(false);
  
  useEffect(() => {
    if (items.length > 0 && Object.keys(materialsByType).length > 0) {
      generateMaterialSummary();
    }
  }, [items, materialsByType]);
  
  const generateMaterialSummary = () => {
    // Initialize structure to hold aggregated material quantities
    const aggregatedMaterials = {
      bottles: {},
      corks: {},
      capsules: {},
      labels: {}
    };
    
    // Process each item to calculate material requirements
    items.forEach(item => {
      // Calculate for each material type
      // Calculate for each material type
    const calculateForMaterial = (materialType, materialId, quantityFactor = 12) => {
      if (!materialId) return;
      
      const materialIdNum = parseInt(materialId);
      const material = materialsByType[materialType]?.find(m => m.materialID === materialIdNum);
      
      if (!material) return;
      
      // Get estimated cases for this item
      const casesEstimated = parseInt(item.casesEstimated) || 0;
      
      // Special handling for labels
      if (materialType === 'labels') {
        // Labels are ordered specifically for each bottling with a buffer
        // Labels needed = bottles needed + 50 extra
        const labelUnitsNeeded = (casesEstimated * quantityFactor) + 50;
        
        // Group by wine ID rather than label ID for labels
        const labelKey = `${materialIdNum}-${item.wineID}`;
        
        if (!aggregatedMaterials[materialType][labelKey]) {
          aggregatedMaterials[materialType][labelKey] = {
            materialID: materialIdNum,
            wineID: item.wineID,
            wineName: item.varietal || `Wine ID: ${item.wineID}`,
            name: `${material.name} - ${item.varietal || `Wine ID: ${item.wineID}`}`,
            description: material.description || '',
            unitOfMeasure: material.unitOfMeasure || 'Each',
            sku: material.sku || 'N/A',
            vendorName: material.vendorName || 'Unknown',
            currentStock: 0, // Labels don't use inventory
            requiredQuantity: 0,
            additionalNeeded: 0,
            items: []
          };
        }
        
        // Update required quantity
        aggregatedMaterials[materialType][labelKey].requiredQuantity += labelUnitsNeeded;
        // For labels, additionalNeeded is always the same as requiredQuantity
        aggregatedMaterials[materialType][labelKey].additionalNeeded = aggregatedMaterials[materialType][labelKey].requiredQuantity;
        
        // Track which items use this material
        aggregatedMaterials[materialType][labelKey].items.push({
          itemID: item.itemID,
          wine: item.varietal || `Wine ID: ${item.wineID}`,
          cases: casesEstimated,
          quantity: labelUnitsNeeded
        });
        
        return; // Exit early for labels
      }
      
      // Check if this is a large format container (like a keg)
      const bottleVolume = materialType === 'bottles' ? (parseFloat(material.bottleVolume) || 750) : 0;
      const isLargeFormat = bottleVolume >= 3000;
      
      // Calculate units needed based on container type
      let materialUnitsNeeded;
      if (materialType === 'bottles' && isLargeFormat) {
        materialUnitsNeeded = casesEstimated; // For kegs, the casesEstimated is already the number of units
      } else {
        materialUnitsNeeded = casesEstimated * quantityFactor; // For regular bottles/materials
      }
      
      // Convert to appropriate unit of measure if needed
      const isCase = material.unitOfMeasure === "Case";
      if (isCase && !isLargeFormat) {
        const unitsPerCase = parseInt(material.casePack) || 1000;
        materialUnitsNeeded = Math.ceil(materialUnitsNeeded / unitsPerCase);
      }
      
      // Add to aggregated total
      if (!aggregatedMaterials[materialType][materialIdNum]) {
        aggregatedMaterials[materialType][materialIdNum] = {
          materialID: materialIdNum,
          name: material.name,
          description: material.description || '',
          unitOfMeasure: material.unitOfMeasure || 'Each',
          sku: material.sku || 'N/A',
          vendorName: material.vendorName || 'Unknown',
          currentStock: parseFloat(material.currentStock) || 0,
          requiredQuantity: 0,
          additionalNeeded: 0,
          items: []
        };
      }
      
      // Update required quantity
      aggregatedMaterials[materialType][materialIdNum].requiredQuantity += materialUnitsNeeded;
      
      // Track which items use this material
      aggregatedMaterials[materialType][materialIdNum].items.push({
        itemID: item.itemID,
        wine: item.varietal || `Wine ID: ${item.wineID}`,
        cases: casesEstimated,
        quantity: materialUnitsNeeded
      });
    };
      
      // Calculate for each material type
      calculateForMaterial('bottles', item.bottleID);
      calculateForMaterial('corks', item.corkID);
      calculateForMaterial('capsules', item.capsuleID);
      calculateForMaterial('labels', item.labelID);
    });
    
    // Convert aggregated objects to arrays and calculate additional needed
    const summarizedMaterials = {};
    
    Object.keys(aggregatedMaterials).forEach(materialType => {
      summarizedMaterials[materialType] = Object.values(aggregatedMaterials[materialType])
        .map(material => ({
          ...material,
          additionalNeeded: materialType === 'labels' ? material.requiredQuantity : 
                           Math.max(0, material.requiredQuantity - material.currentStock)
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    });
    
    setMaterialSummary(summarizedMaterials);
    
    // Initialize checked state for new summary
    const newCheckedItems = {};
    Object.keys(summarizedMaterials).forEach(materialType => {
      summarizedMaterials[materialType].forEach(material => {
        // Auto-check items that need ordering (additional needed > 0 or labels)
        const key = materialType === 'labels' && material.wineID ? 
          `${materialType}-${material.materialID}-${material.wineID}` : 
          `${materialType}-${material.materialID}`;
        
        if (material.additionalNeeded > 0 || materialType === 'labels') {
          newCheckedItems[key] = true;
        }
      });
    });
    setCheckedItems(newCheckedItems);
  };
  
  const handleCheckItem = (materialType, materialID) => {
    // materialID could be a combined string for labels
    const key = materialType === 'labels' && materialID.includes('-') ? 
      `${materialType}-${materialID}` : 
      `${materialType}-${materialID}`;
      
    setCheckedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  const handlePrint = () => {
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setShowPrintView(false);
    }, 100);
  };
  
  const handleExportCSV = () => {
    // Prepare CSV content with all selected materials
    let csvContent = "Material Type,SKU,Name,Vendor,Current Stock,Required,Additional Needed,Unit of Measure\n";
    
    Object.keys(materialSummary).forEach(materialType => {
      materialSummary[materialType].forEach(material => {
        const key = `${materialType}-${material.materialID}`;
        if (checkedItems[key]) {
          csvContent += [
            materialType,
            material.sku,
            material.name,
            material.vendorName,
            material.currentStock,
            material.requiredQuantity,
            material.additionalNeeded,
            material.unitOfMeasure
          ].join(',') + "\n";
        }
      });
    });
    
    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'bottling_materials_order.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleGenerateOrder = () => {
    // Create order summary from checked items
    const orderItems = [];
    
    Object.keys(materialSummary).forEach(materialType => {
      materialSummary[materialType].forEach(material => {
        // For labels, use a different key format including the wineID
        const key = materialType === 'labels' && material.wineID ? 
          `${materialType}-${material.materialID}-${material.wineID}` : 
          `${materialType}-${material.materialID}`;
          
        if (checkedItems[key]) {
          const orderItem = {
            materialID: material.materialID,
            materialType,
            name: material.name,
            sku: material.sku,
            vendorName: material.vendorName,
            currentStock: material.currentStock,
            requiredQuantity: material.requiredQuantity,
            orderQuantity: material.additionalNeeded,
            unitOfMeasure: material.unitOfMeasure
          };
          
          // Add wine-specific data for labels
          if (materialType === 'labels' && material.wineID) {
            orderItem.wineID = material.wineID;
            orderItem.wineName = material.wineName;
            orderItem.isLabelOrder = true;
          }
          
          orderItems.push(orderItem);
        }
      });
    });
    
    // Call parent component's handler with order data
    if (onGenerateOrder) {
      onGenerateOrder(orderItems);
    }
  };
  
  // Helper function to determine if any material needs ordering
  const hasAdditionalNeeded = (materialType) => {
    return materialSummary[materialType].some(m => m.additionalNeeded > 0);
  };
  
  // Determine if any items are checked for ordering
  const hasCheckedItems = Object.values(checkedItems).some(Boolean);
  
  // If there are no summary items yet, show loading or empty state
  if (Object.values(materialSummary).every(arr => arr.length === 0)) {
    return (
      <div className="card mb-4">
        <div className="card-header">
          <h5>Materials Summary</h5>
        </div>
        <div className="card-body">
          <p className="text-center text-muted">
            {items.length === 0 
              ? "Add bottling items to see materials summary" 
              : "Loading materials summary..."}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <div className={`card mb-4 ${showPrintView ? 'd-none' : ''}`}>
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Materials Summary</h5>
          <div className="btn-group">
            <button 
              type="button" 
              className="btn btn-sm btn-outline-secondary"
              onClick={handlePrint}
              title="Print summary"
            >
              <Printer size={16} className="me-1" />
              Print
            </button>
            <button 
              type="button" 
              className="btn btn-sm btn-outline-secondary"
              onClick={handleExportCSV}
              title="Export selected to CSV"
              disabled={!hasCheckedItems}
            >
              <Download size={16} className="me-1" />
              Export
            </button>
            <button 
              type="button" 
              className="btn btn-sm btn-outline-primary"
              onClick={handleGenerateOrder}
              title="Generate material order"
              disabled={!hasCheckedItems}
            >
              <ShoppingCart size={16} className="me-1" />
              Generate Order
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="accordion" id="materialSummaryAccordion">
            {/* Bottles Section */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="bottlesHeading">
                <button 
                  className="accordion-button" 
                  type="button" 
                  data-bs-toggle="collapse" 
                  data-bs-target="#bottlesCollapse" 
                  aria-expanded="true" 
                  aria-controls="bottlesCollapse"
                >
                  Bottles
                  {hasAdditionalNeeded('bottles') && (
                    <span className="badge bg-warning ms-2">Items to Order</span>
                  )}
                </button>
              </h2>
              <div 
                id="bottlesCollapse" 
                className="accordion-collapse collapse show" 
                aria-labelledby="bottlesHeading"
              >
                <div className="accordion-body p-0">
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th width="40">
                            <div className="d-flex align-items-center justify-content-center">
                              <input 
                                type="checkbox" 
                                className="form-check-input"
                                onChange={() => {/* Add bulk check functionality */}}
                                title="Select all bottles"
                              />
                            </div>
                          </th>
                          <th>Bottle</th>
                          <th>Vendor</th>
                          <th width="120">SKU</th>
                          <th width="100" className="text-end">Current Stock</th>
                          <th width="80" className="text-end">Required</th>
                          <th width="80" className="text-end">To Order</th>
                          <th width="80">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {materialSummary.bottles.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-3">No bottles required for this plan</td>
                          </tr>
                        ) : (
                          materialSummary.bottles.map(bottle => {
                            const key = `bottles-${bottle.materialID}`;
                            const isChecked = !!checkedItems[key];
                            const hasShortage = bottle.additionalNeeded > 0;
                            
                            return (
                              <tr 
                                key={bottle.materialID}
                                className={hasShortage ? 'table-warning' : ''}
                              >
                                <td className="text-center">
                                  <input 
                                    type="checkbox" 
                                    className="form-check-input"
                                    checked={isChecked}
                                    onChange={() => handleCheckItem('bottles', bottle.materialID)}
                                  />
                                </td>
                                <td>
                                  {bottle.name}
                                  {bottle.description && (
                                    <small className="d-block text-muted">{bottle.description}</small>
                                  )}
                                </td>
                                <td>{bottle.vendorName}</td>
                                <td>{bottle.sku}</td>
                                <td className="text-end">{bottle.currentStock} {bottle.unitOfMeasure}</td>
                                <td className="text-end">{bottle.requiredQuantity} {bottle.unitOfMeasure}</td>
                                <td className="text-end">
                                  {bottle.additionalNeeded > 0 ? (
                                    <span className="text-danger fw-bold">
                                      {bottle.additionalNeeded} {bottle.unitOfMeasure}
                                    </span>
                                  ) : (
                                    <span className="text-success">0</span>
                                  )}
                                </td>
                                <td>
                                  {bottle.additionalNeeded > 0 ? (
                                    <span className="badge bg-danger">
                                      <X size={14} className="me-1" />
                                      Need to Order
                                    </span>
                                  ) : (
                                    <span className="badge bg-success">
                                      <Check size={14} className="me-1" />
                                      In Stock
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Corks Section */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="corksHeading">
                <button 
                  className="accordion-button collapsed" 
                  type="button" 
                  data-bs-toggle="collapse" 
                  data-bs-target="#corksCollapse" 
                  aria-expanded="false" 
                  aria-controls="corksCollapse"
                >
                  Corks
                  {hasAdditionalNeeded('corks') && (
                    <span className="badge bg-warning ms-2">Items to Order</span>
                  )}
                </button>
              </h2>
              <div 
                id="corksCollapse" 
                className="accordion-collapse collapse" 
                aria-labelledby="corksHeading"
              >
                <div className="accordion-body p-0">
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th width="40">
                            <div className="d-flex align-items-center justify-content-center">
                              <input 
                                type="checkbox" 
                                className="form-check-input"
                                onChange={() => {/* Add bulk check functionality */}}
                                title="Select all corks"
                              />
                            </div>
                          </th>
                          <th>Cork</th>
                          <th>Vendor</th>
                          <th width="120">SKU</th>
                          <th width="100" className="text-end">Current Stock</th>
                          <th width="80" className="text-end">Required</th>
                          <th width="80" className="text-end">To Order</th>
                          <th width="80">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {materialSummary.corks.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-3">No corks required for this plan</td>
                          </tr>
                        ) : (
                          materialSummary.corks.map(cork => {
                            const key = `corks-${cork.materialID}`;
                            const isChecked = !!checkedItems[key];
                            const hasShortage = cork.additionalNeeded > 0;
                            
                            return (
                              <tr 
                                key={cork.materialID}
                                className={hasShortage ? 'table-warning' : ''}
                              >
                                <td className="text-center">
                                  <input 
                                    type="checkbox" 
                                    className="form-check-input"
                                    checked={isChecked}
                                    onChange={() => handleCheckItem('corks', cork.materialID)}
                                  />
                                </td>
                                <td>
                                  {cork.name}
                                  {cork.description && (
                                    <small className="d-block text-muted">{cork.description}</small>
                                  )}
                                </td>
                                <td>{cork.vendorName}</td>
                                <td>{cork.sku}</td>
                                <td className="text-end">{cork.currentStock} {cork.unitOfMeasure}</td>
                                <td className="text-end">{cork.requiredQuantity} {cork.unitOfMeasure}</td>
                                <td className="text-end">
                                  {cork.additionalNeeded > 0 ? (
                                    <span className="text-danger fw-bold">
                                      {cork.additionalNeeded} {cork.unitOfMeasure}
                                    </span>
                                  ) : (
                                    <span className="text-success">0</span>
                                  )}
                                </td>
                                <td>
                                  {cork.additionalNeeded > 0 ? (
                                    <span className="badge bg-danger">
                                      <X size={14} className="me-1" />
                                      Need to Order
                                    </span>
                                  ) : (
                                    <span className="badge bg-success">
                                      <Check size={14} className="me-1" />
                                      In Stock
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Capsules Section */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="capsulesHeading">
                <button 
                  className="accordion-button collapsed" 
                  type="button" 
                  data-bs-toggle="collapse" 
                  data-bs-target="#capsulesCollapse" 
                  aria-expanded="false" 
                  aria-controls="capsulesCollapse"
                >
                  Capsules
                  {hasAdditionalNeeded('capsules') && (
                    <span className="badge bg-warning ms-2">Items to Order</span>
                  )}
                </button>
              </h2>
              <div 
                id="capsulesCollapse" 
                className="accordion-collapse collapse" 
                aria-labelledby="capsulesHeading"
              >
                <div className="accordion-body p-0">
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th width="40">
                            <div className="d-flex align-items-center justify-content-center">
                              <input 
                                type="checkbox" 
                                className="form-check-input"
                                onChange={() => {/* Add bulk check functionality */}}
                                title="Select all capsules"
                              />
                            </div>
                          </th>
                          <th>Capsule</th>
                          <th>Vendor</th>
                          <th width="120">SKU</th>
                          <th width="100" className="text-end">Current Stock</th>
                          <th width="80" className="text-end">Required</th>
                          <th width="80" className="text-end">To Order</th>
                          <th width="80">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {materialSummary.capsules.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-3">No capsules required for this plan</td>
                          </tr>
                        ) : (
                          materialSummary.capsules.map(capsule => {
                            const key = `capsules-${capsule.materialID}`;
                            const isChecked = !!checkedItems[key];
                            const hasShortage = capsule.additionalNeeded > 0;
                            
                            return (
                              <tr 
                                key={capsule.materialID}
                                className={hasShortage ? 'table-warning' : ''}
                              >
                                <td className="text-center">
                                  <input 
                                    type="checkbox" 
                                    className="form-check-input"
                                    checked={isChecked}
                                    onChange={() => handleCheckItem('capsules', capsule.materialID)}
                                  />
                                </td>
                                <td>
                                  {capsule.name}
                                  {capsule.description && (
                                    <small className="d-block text-muted">{capsule.description}</small>
                                  )}
                                </td>
                                <td>{capsule.vendorName}</td>
                                <td>{capsule.sku}</td>
                                <td className="text-end">{capsule.currentStock} {capsule.unitOfMeasure}</td>
                                <td className="text-end">{capsule.requiredQuantity} {capsule.unitOfMeasure}</td>
                                <td className="text-end">
                                  {capsule.additionalNeeded > 0 ? (
                                    <span className="text-danger fw-bold">
                                      {capsule.additionalNeeded} {capsule.unitOfMeasure}
                                    </span>
                                  ) : (
                                    <span className="text-success">0</span>
                                  )}
                                </td>
                                <td>
                                  {capsule.additionalNeeded > 0 ? (
                                    <span className="badge bg-danger">
                                      <X size={14} className="me-1" />
                                      Need to Order
                                    </span>
                                  ) : (
                                    <span className="badge bg-success">
                                      <Check size={14} className="me-1" />
                                      In Stock
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Labels Section */}
            <div className="accordion-item">
              <h2 className="accordion-header" id="labelsHeading">
                <button 
                  className="accordion-button collapsed" 
                  type="button" 
                  data-bs-toggle="collapse" 
                  data-bs-target="#labelsCollapse" 
                  aria-expanded="false" 
                  aria-controls="labelsCollapse"
                >
                  Labels (Ordered Per Wine)
                  <span className="badge bg-warning ms-2">Always Ordered</span>
                </button>
              </h2>
              <div 
                id="labelsCollapse" 
                className="accordion-collapse collapse" 
                aria-labelledby="labelsHeading"
              >
                <div className="accordion-body p-0">
                  <div className="alert alert-info m-3">
                    <small>Labels are ordered specifically for each wine in the bottling plan with 50 extra labels as buffer.</small>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th width="40">
                            <div className="d-flex align-items-center justify-content-center">
                              <input 
                                type="checkbox" 
                                className="form-check-input"
                                onChange={() => {/* Add bulk check functionality */}}
                                title="Select all labels"
                              />
                            </div>
                          </th>
                          <th>Label</th>
                          <th>Wine</th>
                          <th>Vendor</th>
                          <th width="120">SKU</th>
                          <th width="80" className="text-end">Required</th>
                          <th width="80" className="text-end">To Order</th>
                          <th width="80">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {materialSummary.labels.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-3">No labels required for this plan</td>
                          </tr>
                        ) : (
                          materialSummary.labels.map(label => {
                            const key = `labels-${label.materialID}-${label.wineID}`;
                            const isChecked = !!checkedItems[key];
                            
                            return (
                              <tr 
                                key={key}
                                className="table-warning" // Always highlight labels
                              >
                                <td className="text-center">
                                  <input 
                                    type="checkbox" 
                                    className="form-check-input"
                                    checked={isChecked}
                                    onChange={() => handleCheckItem('labels', `${label.materialID}-${label.wineID}`)}
                                  />
                                </td>
                                <td>
                                  {label.name}
                                  {label.description && (
                                    <small className="d-block text-muted">{label.description}</small>
                                  )}
                                </td>
                                <td>{label.wineName}</td>
                                <td>{label.vendorName}</td>
                                <td>{label.sku}</td>
                                <td className="text-end">{label.requiredQuantity} {label.unitOfMeasure}</td>
                                <td className="text-end">
                                  <span className="text-danger fw-bold">
                                    {label.requiredQuantity} {label.unitOfMeasure}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge bg-danger">
                                    <X size={14} className="me-1" />
                                    Need to Order
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Print View */}
      {/* Print View */}
      {showPrintView && (
        <div className="print-only">
          <h2 className="text-center mb-3">Bottling Materials Summary</h2>
          <p className="text-center mb-4">
            <small>Generated on {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</small>
          </p>
          
          {/* Show all material types, even if they're not checked */}
          {Object.keys(materialSummary).map(materialType => {
            // Include all materials for this type with quantities > 0
            const materialsOfType = materialSummary[materialType].filter(m => 
              m.requiredQuantity > 0
            );
            
            if (materialsOfType.length === 0) return null;
            
            return (
              <div key={materialType} className="mb-3">
                <h4 className="mb-2 text-capitalize">{materialType}</h4>
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Material</th>
                      <th>SKU</th>
                      <th>Vendor</th>
                      <th className="text-center">Required</th>
                      <th className="text-center">Current Stock</th>
                      <th className="text-center">To Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialsOfType.map(material => {
                      // For labels, handle special key format that includes wineID
                      const key = materialType === 'labels' && material.wineID ? 
                        `${materialType}-${material.materialID}-${material.wineID}` : 
                        `${materialType}-${material.materialID}`;
                      
                      // Bold if checked for ordering
                      const isChecked = !!checkedItems[key];
                      const needsOrdering = material.additionalNeeded > 0 || materialType === 'labels';
                      
                      return (
                        <tr key={key} className={needsOrdering ? "table-warning" : ""}>
                          <td className={isChecked ? "fw-bold" : ""}>
                            {material.name}
                            {materialType === 'labels' && material.wineName && (
                              <div><small>For: {material.wineName}</small></div>
                            )}
                          </td>
                          <td>{material.sku}</td>
                          <td>{material.vendorName}</td>
                          <td className="text-center">{material.requiredQuantity} {material.unitOfMeasure}</td>
                          <td className="text-center">{material.currentStock} {material.unitOfMeasure}</td>
                          <td className="text-center fw-bold">
                            {material.additionalNeeded > 0 || materialType === 'labels' ? (
                              <span>{material.additionalNeeded} {material.unitOfMeasure}</span>
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
      
      <style jsx="true">{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-only, .print-only * {
            visibility: visible;
          }
          .print-only {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
          }
          th {
            background-color: #f2f2f2;
          }
        }
        
        @media screen {
          .print-only {
            display: none;
          }
        }
        
        .table-responsive {
          max-height: 400px;
          overflow-y: auto;
        }
        
        .form-check-input:checked {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
        
        .accordion-button:not(.collapsed)::after {
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%230c63e4'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
        }
        
        .badge.bg-warning {
          background-color: #ffc107 !important;
          color: #212529 !important;
        }
        
        .table-warning {
          background-color: rgba(255, 193, 7, 0.2) !important;
        }
      `}</style>
    </>
  );
};

export default MaterialsSummary;